import {
  CoderbyteWorkspaceFieldsFragment,
  CoderbyteWorkspaceStatus,
  CoderbyteWorkspaceUserStatus,
} from 'generated/graphql';
import { formatTimestamp } from 'helpers/date';
import { GridCellParams } from '@spotted-zebra-uk/ui';
import { Tag, TagColor, TagSize } from '@spotted-zebra-uk/ui-components';

const workspaceStatusInfo: Record<
  CoderbyteWorkspaceStatus,
  { label: string; color: TagColor }
> = {
  [CoderbyteWorkspaceStatus.PendingSetup]: {
    label: 'Awaiting setup',
    color: TagColor.YELLOW_BACK,
  },
  [CoderbyteWorkspaceStatus.Active]: {
    label: 'Active',
    color: TagColor.GREEN,
  },
  [CoderbyteWorkspaceStatus.Suspended]: {
    label: 'Suspended',
    color: TagColor.DISABLED,
  },
};

const userStatusInfo: Record<
  CoderbyteWorkspaceUserStatus,
  { label: string; color: TagColor }
> = {
  // Deliberately not "Invited": Coderbyte holds the invitation, but no account
  // exists there until the user follows the link. The distinction is the first
  // thing you want when working out why a hand-off failed.
  [CoderbyteWorkspaceUserStatus.Invited]: {
    label: 'Not yet signed in',
    color: TagColor.YELLOW_BACK,
  },
  [CoderbyteWorkspaceUserStatus.Active]: {
    label: 'Active',
    color: TagColor.GREEN,
  },
  [CoderbyteWorkspaceUserStatus.Revoked]: {
    label: 'Revoked',
    color: TagColor.DISABLED,
  },
};

export function WorkspaceStatusTag({
  status,
}: {
  status: CoderbyteWorkspaceStatus;
}) {
  const { label, color } = workspaceStatusInfo[status];

  return (
    <Tag
      isDismissible={false}
      isPresentational
      tagColor={color}
      size={TagSize.LARGE}
    >
      {label}
    </Tag>
  );
}

export function UserStatusTag({
  status,
}: {
  status: CoderbyteWorkspaceUserStatus;
}) {
  const { label, color } = userStatusInfo[status];

  return (
    <Tag
      isDismissible={false}
      isPresentational
      tagColor={color}
      size={TagSize.LARGE}
    >
      {label}
    </Tag>
  );
}

export type CoderbyteWorkspaceRow = {
  id: number;
  companyId: number;
  companyName: string;
  externalId: string;
  status: CoderbyteWorkspaceStatus;
  keyVerifiedAt: string;
  createdAt: string;
};

export function parseWorkspaceRows(
  workspaces: CoderbyteWorkspaceFieldsFragment[] | undefined
): CoderbyteWorkspaceRow[] {
  if (!workspaces) return [];

  return workspaces.map(workspace => ({
    id: workspace.id,
    companyId: workspace.companyId,
    companyName: workspace.companyName,
    externalId: workspace.externalId,
    status: workspace.status,
    keyVerifiedAt: formatTimestamp(workspace.keyVerifiedAt),
    createdAt: formatTimestamp(workspace.createdAt),
  }));
}

export const WORKSPACE_COLUMNS = [
  { field: 'companyName', headerName: 'Company', flex: 0.4, minWidth: 200 },
  {
    field: 'externalId',
    headerName: 'Organization ID',
    flex: 0.3,
    minWidth: 200,
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 180,
    renderCell: (params: GridCellParams) => (
      <WorkspaceStatusTag status={params.value as CoderbyteWorkspaceStatus} />
    ),
  },
  { field: 'keyVerifiedAt', headerName: 'Key verified', minWidth: 140 },
  { field: 'createdAt', headerName: 'Added', minWidth: 140 },
];
